import UdeskLocales from 'UdeskLocales';
import React, { FC, memo, useState } from 'react';
import { Avatar, Button, Empty, Input, List, Popconfirm, Popover, Space } from 'udesk-ui';
import { CloseOutlined, DeleteBinOutlined, MessageFilled, UserOutlined } from '@udesk/icons';
import styled from 'styled-components';
import ReadAndWrite from './components/ReadAndWrite';
import { postInspectionDataPointNotes } from 'src/api/inspectionDataPointNotes';
import {
    putInspectionDataPointNotesById,
    deleteInspectionDataPointNotesById,
} from 'src/api/inspectionDataPointNotes/{id}';
import { InspectionDataPointNoteFoundResponse } from 'src/api/types';

const TitleWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const ContentWrapper = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: stretch;
    gap: 4px;
    width: 360px;
`;

const ContentMain = styled.div`
    max-height: 200px;
    overflow-y: scroll;

    .udesk-qa-ui-list-item-action {
        margin-left: 0;
    }
`;

const ContentFooter = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: center;
`;

const UserName = styled.div`
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    flex: 100px 0 0;
`;

const Time = styled.div`
    font-size: 12px;
    color: rgba(0, 0, 0, 0.25);
`;

interface TemplateProps {
    id?: number;
    dataId?: number;
    pointId?: number;
    readonly?: boolean;
    shape?: 'button' | 'icon';
    isComplain?: boolean;
    sdkOptions?: any;
    data?: InspectionDataPointNoteFoundResponse[];
    refresh?: () => void;
}

const Template: FC<TemplateProps> = memo((props) => {
    const {
        id,
        dataId,
        pointId,
        readonly,
        shape = 'icon',
        isComplain,
        data,
        refresh,
    } = props || {};

    const [visible, setVisible] = useState(false);

    const onOpen = () => {
        setVisible(true);
    };

    const onClose = () => {
        setVisible(false);
    };

    const [newContent, setNewContent] = useState<string>();

    const onNewChange = (c?: string) => {
        setNewContent(c);
    };

    const onAdd = () => {
        const content = newContent?.trim();

        if (id && content) {
            postInspectionDataPointNotes({
                dataPointId: id,
                dataId: dataId,
                pointId: pointId,
                content,
            }).then(() => {
                setNewContent('');
                refresh?.();
            });
        }
    };

    const onModify = ({ id, content: _content }: { id?: number; content?: string }) => {
        const content = _content?.trim();

        if (id && content) {
            putInspectionDataPointNotesById({ content }, { segments: { id } }).finally(() => {
                refresh?.();
            });
        }
    };

    const onDelete = (id?: number) => {
        if (id) {
            deleteInspectionDataPointNotesById({ segments: { id } }).then(() => {
                refresh?.();
            });
        }
    };

    return (
        <Popover
            title={
                <TitleWrapper>
                    <div>
                        {/* 规则批注 */}
                        {
                            UdeskLocales['current'].components.qualityScore.components.annotation
                                .index.ruleAnnotation
                        }
                    </div>
                    <Button
                        icon={<CloseOutlined />}
                        type="text"
                        size="small"
                        onClick={() => onClose()}
                    />
                </TitleWrapper>
            }
            trigger={['click']}
            placement="bottomRight"
            destroyTooltipOnHide
            visible={visible}
            content={
                <ContentWrapper>
                    <ContentMain>
                        {data?.length ? (
                            <List
                                itemLayout="horizontal"
                                size="small"
                                dataSource={data}
                                renderItem={(item) => (
                                    <List.Item
                                        actions={[
                                            <Popconfirm
                                                title={
                                                    /* 确定删除吗？ */ UdeskLocales['current']
                                                        .components.qualityScore.components
                                                        .annotation.index.areYouSureToDeleteIt
                                                }
                                                disabled={!item.canEdit || readonly}
                                                onConfirm={() => onDelete(item.id)}
                                            >
                                                <Button
                                                    type="text"
                                                    icon={<DeleteBinOutlined />}
                                                    danger
                                                    size="small"
                                                    disabled={!item.canEdit || readonly}
                                                />
                                            </Popconfirm>,
                                        ]}
                                    >
                                        <List.Item.Meta
                                            avatar={
                                                isComplain &&
                                                item.complainInspectorHidden ? null : (
                                                    <Avatar
                                                        shape="square"
                                                        size="small"
                                                        src={item.headPortraitUrl}
                                                        icon={<UserOutlined />}
                                                    />
                                                )
                                            }
                                            title={
                                                <TitleWrapper>
                                                    <UserName title={item.createUserUsername}>
                                                        {isComplain && item.complainInspectorHidden
                                                            ? null
                                                            : item.createUserUsername}
                                                    </UserName>

                                                    <Time>{item.createTime}</Time>
                                                </TitleWrapper>
                                            }
                                            description={
                                                <ReadAndWrite
                                                    value={item.content}
                                                    onBlur={(v) =>
                                                        item.canEdit &&
                                                        !readonly &&
                                                        onModify({ id: item.id, content: v })
                                                    }
                                                    readOnly={!item.canEdit || readonly}
                                                />
                                            }
                                        />
                                    </List.Item>
                                )}
                            />
                        ) : (
                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                        )}
                    </ContentMain>

                    {readonly ? null : (
                        <>
                            <div>
                                <Input.TextArea
                                    rows={2}
                                    value={newContent}
                                    onChange={(e) => onNewChange(e.target.value)}
                                />
                            </div>

                            <ContentFooter>
                                <Space>
                                    <Button type="primary" size="small" onClick={() => onAdd()}>
                                        {/* 保存 */}
                                        {
                                            UdeskLocales['current'].components.qualityScore
                                                .components.annotation.index.preserve
                                        }
                                    </Button>
                                    <Button size="small" onClick={() => onClose()}>
                                        {/* 取消 */}
                                        {
                                            UdeskLocales['current'].components.qualityScore
                                                .components.annotation.index.cancel
                                        }
                                    </Button>
                                </Space>
                            </ContentFooter>
                        </>
                    )}
                </ContentWrapper>
            }
        >
            {shape === 'icon' ? (
                <Button
                    icon={<MessageFilled />}
                    type="link"
                    size="small"
                    tooltip={
                        /* 批注 */ UdeskLocales['current'].components.qualityScore.components
                            .annotation.index.annotation
                    }
                    onClick={() => onOpen()}
                />
            ) : shape === 'button' ? (
                <Button type="primary" size="small" onClick={() => onOpen()}>
                    {/* 批注 */}
                    {
                        UdeskLocales['current'].components.qualityScore.components.annotation.index
                            .annotation
                    }
                </Button>
            ) : null}
        </Popover>
    );
});

export default Template;
